import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, } from 'rxjs/operators';

import { MotesPaths, Mote, MediaType, Challenge, ProductType, Project, ServiceId } from '../../models';
import { TourInfo } from '../../models/tour-info';
import { MoteService } from '../../providers/mote.service';
import { QuizService } from '../../providers/quiz.service';
import { DatabaseService } from '../../providers/database.service';
import { TourInfoAutogen } from './tour-info-autogen';
import { ProductVoucherList, ProjectService, VoucherService } from '../../providers';
import { MotesLoginService } from '../../motes-login';

@Injectable({ providedIn: 'root' })
export class TourInfoService {


    constructor(private moteService: MoteService, private db: DatabaseService, private quizService: QuizService, private projectService: ProjectService, private loginService: MotesLoginService, private voucherService: VoucherService) { }

    public watchTourInfo(tourKey: string, previewKey?: string, serviceId?: ServiceId) : Observable<[TourInfo, Mote]> {

        let tourMap: { tour: Mote, children: Mote[], challenge: Challenge, productVoucherLists: ProductVoucherList[], project: Project };

        return this.getTourObservables(tourKey, previewKey).pipe(map((tuple) => {
            tourMap = { tour: tuple[0], children: tuple[1], challenge: tuple[2], productVoucherLists: tuple[3], project: tuple[4] };
            if (!tourMap.tour || !tourMap.tour.doesExist) throw new Error("Tour does not exist");

            // Get external tour information
            const tourHasChallenge = Boolean(tourMap.challenge);
            const tourHasSound = tourMap.children.some((mote) => mote.mediaArray.some((media) => media.type == MediaType.SOUND));
            // Get user's tour voucher if there is one
            const isTourVoucher = (productVoucherList: ProductVoucherList) => !productVoucherList.product.parent && productVoucherList.product.type == ProductType.Tour;
            const productVoucherList: ProductVoucherList = tourMap.productVoucherLists?.find((productVoucherList) => isTourVoucher(productVoucherList) && productVoucherList.product.tourKey == tourMap.tour.key);
            const info = TourInfoAutogen.generateTourInfo(tourMap.tour, tourMap.tour?.storyCount, tourHasChallenge, tourHasSound, tourMap.project?.creatorPlan, productVoucherList?.vouchers[0], serviceId);
            return [info, tourMap.tour];
        }));
    }

    /**
     * Combine observables for every information needed on the tour
     */
    private getTourObservables(tourKey: string, previewKey: string): Observable<[ tour: Mote, children: Mote[], challenge: Challenge, productVoucherLists: ProductVoucherList[], project: Project ]> {
        const watchTour = this.moteService.watchMotePreview(tourKey, previewKey);
        const watchChallenge = this.quizService.getTourChallenge(tourKey);
        const watchUserVouchers = watchTour.pipe(switchMap((tour) => !tour?.productLocked ? of([]) : this.loginService.watchUserRights().pipe(switchMap((userRights) => this.voucherService.watchProductVoucherLists(userRights?.uid)))));
        const watchChildren = watchTour.pipe(switchMap((tour) => !tour ? of([]) : this.moteService.watchChildren(tour, true)));
        const watchProject = watchTour.pipe(switchMap((tour) => !tour ? of(null) : this.projectService.watchProject(tour?.project)));

        return combineLatest([watchTour, watchChildren, watchChallenge, watchUserVouchers, watchProject]);
    }

    public watchAllTourInfo(): Observable<TourInfo[]> {
        return this.db.list(MotesPaths.TOUR_INFO_DB_PATH()).snapshotChanges().pipe(map(snapArr => {
            return snapArr.map(json => new TourInfo(json.key, json.payload.val()));
        }));
    }

    public async saveTourInfo(tourInfo: TourInfo): Promise<void> {
        if(!tourInfo || !tourInfo.key) {
            console.warn("Tour info could not be saved, key does not exist", tourInfo);
            return;
        }
        return this.db.object(MotesPaths.TOUR_INFO_DB_PATH(tourInfo.key)).set(tourInfo.toJson());
    }
}
